import { Season, TID_LIST } from '@shared/constants';
import {
  FieldTurfType,
  IEnhancedTeamStats,
  IGamePredictionBase,
  IGameStats,
  ITeamSchedule,
  ITeamWeeklyResult,
  ModelVersionID,
  TeamAbrvID,
  TeamAbrvType,
} from '@shared/types';

// #region Enums
export enum GameWeek {
  Week1 = 'Week1',
  Week2 = 'Week2',
  Week3 = 'Week3',
  Week4 = 'Week4',
  Week5 = 'Week5',
  Week6 = 'Week6',
  Week7 = 'Week7',
  Week8 = 'Week8',
  Week9 = 'Week9',
  Week10 = 'Week10',
  Week11 = 'Week11',
  Week12 = 'Week12',
  Week13 = 'Week13',
  Week14 = 'Week14',
  Week15 = 'Week15',
  Week16 = 'Week16',
  Week17 = 'Week17',
  Week18 = 'Week18',
}

export type SeasonGameIDs = {
  [key: SeasonWeekID]: GameID[];
};

export enum GameNavCategory {
  Game = 'Game',
  Offense = 'Offense',
  Defense = 'Defense',
  SpecialTeams = 'SpecialTeams',
  SeasonComparison = 'SeasonComparison',
  SeasonComparisonDefense = 'SeasonComparisonDefense',
  Efficiency = 'Efficiency',
}

export enum GameNavOffenseCategory {
  ScoringBreakdown = 'scoring breakdown',
  Total = 'total',
  Passing = 'passing',
  Rushing = 'rushing',
  SackedTimes = 'sacks taken',
  InterceptionsThrown = 'INTs thrown',
  FumblesLost = 'fumbles lost',
  Penalties = 'penalties',
  PenaltyYards = 'penalty yards',
}

export enum GameNavDefensiveCategory {
  // PointsAllowed = 'points allowed',
  Sacks = 'sacks',
  Interceptions = 'interceptions',
  FumblesRecovered = 'fumble recovery',
  ScoringAgainstBreakdown = 'scoring allowed',
  YardsAllowed = 'Yards allowed',
  Safeties = 'safety',
  DefensiveTouchdowns = 'defensive TDs',
}

export enum GameNavComparisonCategory {
  TimeOfPossession = 'time of possession',
  ScoringForVsPointsAgainst = 'scoring for vs against',
  TotalYardsForVsAgainst = 'yards for vs against',
  PassingYardsForVsAgainst = 'passing yards for vs against',
  RushingYardsForVsAgainst = 'rushing yards for vs against',
  SacksVsSacksAllowed = 'sacks vs sacks allowed',
  InterceptionsThrownVsInterceptions = 'ints thrown vs ints',
  FumblesLostVsFumblesRecovered = 'fumbles lost vs recovered',
  PenaltiesComparison = 'penalties comparison',
  PenaltyYardsComparison = 'penalty yards comparison',
}

export enum GameNavEfficiencyCategory {
  FirstDownEfficiency = '1st downs',
  ThirdDownEfficiency = '3rd downs',
  FourthDownEfficiency = '4th downs',
  RedZoneEfficiency = 'red zone',
}

export enum GameNavOffensiveEfficiencyCategory {
  FirstDownEfficiency = '1st downs',
  ThirdDownEfficiency = '3rd downs',
  FourthDownEfficiency = '4th downs',
  RedZoneEfficiency = 'red zone',
}

export enum GameNavDefensiveEfficiencyCategory {
  PassIncompletePercentage = 'pass incompletions',
  ThirdDownStopEfficiency = '3rd downs',
  FourthDownStopEfficiency = '4th downs',
  RedZoneStopEfficiency = 'red zone',
}

export type GameNavCategoryType = keyof typeof GameNavCategory;
export type GameNavOffenseCategoryType = keyof typeof GameNavOffenseCategory;
export type GameNavDefensiveCategoryType = keyof typeof GameNavDefensiveCategory;
export type GameNavComparisonCategoryType = keyof typeof GameNavComparisonCategory;
export type GameNavEfficiencyCategoryType = keyof typeof GameNavEfficiencyCategory;
export type GameNavOffensiveEfficiencyCategoryType = keyof typeof GameNavOffensiveEfficiencyCategory;
export type GameNavDefensiveEfficiencyCategoryType = keyof typeof GameNavDefensiveEfficiencyCategory;

export type GameSubNavCategory =
  | GameNavOffenseCategoryType
  | GameNavDefensiveCategoryType
  | GameNavComparisonCategoryType
  | GameNavEfficiencyCategoryType
  | GameNavOffensiveEfficiencyCategoryType
  | GameNavDefensiveEfficiencyCategoryType;

export enum AppScene {
  Authentication = 'Authentication',
  CategoryRankings = 'CategoryRankings',
  Comparison = 'Comparison',
  GameDetails = 'GameDetails',
  HeadToHead = 'HeadToHead',
  Home = 'Home',
  Landing = 'Landing',
  None = 'None',
  Standings = 'Standings',
  TeamDetails = 'TeamDetails',
  Teams = 'Teams',
  WeeklyGames = 'WeeklyGames',
}
// #endregion

// #region Base Types *********************************************************
export type Scene = keyof typeof AppScene;

// | 'authentication'
// | 'gamedetails'
// | 'landing'
// | 'none'
// | 'standings'
// | 'teamdetails'
// | 'teams'
// | 'weeklygames';
export const FormatUnits = {
  yards: 'yards',
  time: 'time',
  points: 'points',
  plays: 'plays',
  int: 'int',
  fum: 'fum',
  attempts: 'attempts',
  completions: 'completions',
  units: 'units',
  percent: 'percent',
  tds: 'tds',
  fgs: 'fgs',
  ratio: 'ratio',
  DNP: 'DNP',
} as const;
export type FormatUnitType = (typeof FormatUnits)[keyof typeof FormatUnits];

export const BYE = 'BYE';
export type BYE_WEEK = typeof BYE;
export type GameWeekKey = keyof typeof GameWeek;
/** consists of {YYYY}-{WeekID} eg 2024-01, 2024-02, ..., 2025-18 */
export type SeasonWeekID = `${SeasonID}-${WeekID}`;
/** {YYYY} a four digit year of the NFL season represented as a string,
 * eg if season is played in 2025-26 the `SeasonID` will be `2025` */
export type SeasonID = Season | string;
export type WeekID = string | GameWeek | GameWeekKey;
/** Bye week identifier, consists of {YYYY}-{WW}-{TT}-{TeamAbrv} where
 * WW is the WeekID number padded with leading 0 if week is less than 10
 * TT is the TeamID padded with leading 0 if team is less than 10 */
export type ByeWeekID = `${SeasonID}-${WeekID}-${TeamID}-${string}`; // last string is teamAbrv
export type CRUD = 'CREATE' | 'DELETE' | 'UPDATE' | 'READ';

export type TID = (typeof TID_LIST)[number];

/**
 * `UID is a combination of 4 values that uniquely identify a game, team,
 * The week,and team abbreviation are used to create a unique identifier for each game
 *  `${weekID}-${teamID}-${teamAbrv}-${gameID}`
 */
export type UID = string;
/** Eg: `20240905_BAL@KC` */
export type GameID = string | BYE_WEEK;

export type ZeroPaddedTeamID =
  | '01'
  | '02'
  | '03'
  | '04'
  | '05'
  | '06'
  | '07'
  | '08'
  | '09'
  | '10'
  | '11'
  | '12'
  | '13'
  | '14'
  | '15'
  | '16'
  | '17'
  | '18'
  | '19'
  | '20'
  | '21'
  | '22'
  | '23'
  | '24'
  | '25'
  | '26'
  | '27'
  | '28'
  | '29'
  | '30'
  | '31'
  | '32';

export type TeamID = string;
// | '1'
// | '2'
// | '3'
// | '4'
// | '5'
// | '6'
// | '7'
// | '8'
// | '9'
// | '10'
// | '11'
// | '12'
// | '13'
// | '14'
// | '15'
// | '16'
// | '17'
// | '18'
// | '19'
// | '20'
// | '21'
// | '22'
// | '23'
// | '24'
// | '25'
// | '26'
// | '27'
// | '28'
// | '29'
// | '30'
// | '31'
// | '32';
export type PlayerID = string;

// // zero padded teamID - TeamAbrv
// export type TeamAbrvID = `${TeamID}-${TeamAbrvType}`;

/** When displaying stats via graph, indicates size of the chart line based on higher ranks being lower or higher than lower ranks
 *
 * @example Total Yards Allowed - High: 1st rank is the lowest value, 32nd rank is the highest value
 */
export type BestRankHighOrLow = 'High' | 'Low';
// #endregion

// #region GAME STATS INTERFACES
export interface IMatchup {
  // seasonWeekID: '2024-01';
  seasonWeekID: SeasonWeekID;
  // gameID: '20240905_BAL@KC';
  gameID: GameID;
  // home: 'KC';
  home: string;
  // away: 'BAL';
  away: string;
  // teamIDHome: '16';
  teamIDHome: string;
  // teamIDAway: '3';
  teamIDAway: string;
  /** Formatted as `Week #` string, so the _space_ needs to be dealt with */
  // gameWeek: 'Week 1';
  gameWeek: string;
  // gameDate: '20240905';
  gameDate: string;
  // gameTime: '8:40p';
  gameTime: string;
  // season: '2024';
  season: string;
  // seasonType: 'Regular Season';
  seasonType: string;
  // neutralSite: 'False';
  neutralSite: string;
  // gameTime_epoch: '1725583200.0';
  gameTime_epoch: string;
  // gameStatus: 'Final';
  gameStatus: string;
  // gameStatusCode: '2';
  gameStatusCode: string;
  // if stats available populate the score for home team
  homePts?: string;
  // if stats available populate the score for away team
  awayPts?: string;
  // if stats available populate the result with W, L, or T for home team
  homeResult?: string;
  // if stats available populate the result with W, L, or T for away team
  awayResult?: string;
  // if stats available populate the line score for the game
  lineScore?: ILineScore;
  // game context enrichment data (weather, rest days, venue info, etc.)
  gameContext?: IGameContextEnrichment;
  /** Prediction score, percentage, and confidence */
  predictions?: Record<ModelVersionID, IGamePredictionBase>;
  // espnID: '401671789';
  espnID?: string;
  // espnLink: 'https://www.espn.com/nfl/boxscore/_/gameId/401671789';
  espnLink?: string;
  // cbsLink: 'https://www.cbssports.com/nfl/gametracker/boxscore/NFL_20240905_BAL@KC';
  cbsLink?: string;
}

export interface IGame extends IMatchup {
  // espnID: '401671789';
  espnID: string;
  // espnLink: 'https://www.espn.com/nfl/boxscore/_/gameId/401671789';
  espnLink: string;
  // cbsLink: 'https://www.cbssports.com/nfl/gametracker/boxscore/NFL_20240905_BAL@KC';
  cbsLink: string;
}

export type GameRecordT = Record<GameID, IMatchup>;

export type GameWeeks = {
  [key in GameWeek]: IGame[];
};

export type Weekly<T> = {
  [key in GameWeek]: T;
};

export type WeeklyRanks = {
  [key in GameWeek]: IRankingCategories;
};

export type WeeklyRanksCalculated = {
  [key in GameWeek]: boolean;
};

export interface ITeamsOnByeWeek {
  seasonWeekID: SeasonWeekID;
  teamIDs: TeamID[];
  weekID: GameWeek;
  year: SeasonID;
}

export interface ByeWeeksInfo {
  weekID: GameWeek;
  seasonYear: SeasonID;
  teamsOnByeWeek: ITeamsOnByeWeek;
  byeWeekTeamsAddedToSeason: ITeamsOnByeWeek;
}

export type TeamsOnByeWeek = {
  [key in GameWeek]: ByeWeeksInfo;
};

export type TeamColorsType = {
  primary: string;
  secondary?: string;
  ternary?: string;
};

export type SeasonByeWeeks = ITeamsOnByeWeek[];

export interface ITeamGameStats {
  [key: string]: unknown;
  defensiveInterceptions: string;
  defensiveOrSpecialTeamsTds: string;
  firstDowns: string;
  firstDownsFromPenalties: string;
  fourthDownEfficiency: string;
  fumblesLost: string;
  interceptionsThrown: string;
  passCompletionsAndAttempts: string;
  passingFirstDowns: string;
  passingYards: string;
  penalties: string;
  pointsScored: number;
  possession: string;
  redZoneScoredAndAttempted: string;
  rushingAttempts: string;
  rushingFirstDowns: string;
  rushingYards: string;
  sacksAndYardsLost: string;
  safeties: string;
  scoring2PtAgainst: number;
  scoring2PtFor: number;
  scoringEPAgainst: number;
  scoringEPFor: number;
  scoringFGAgainst: number;
  scoringFGFor: number;
  scoringPassingTDAgainst: number;
  scoringPassingTDFor: number;
  scoringRushingTDAgainst: number;
  scoringRushingTDFor: number;
  scoringTotalTDAgainst: number;
  scoringTotalTDFor: number;
  team: string;
  teamAbv: string;
  teamID: string;
  thirdDownEfficiency: string;
  totalDrives: string;
  totalPlays: string;
  totalYards: string;
  turnovers: string;
  yardsPerPass: string;
  yardsPerPlay: string;
  yardsPerRush: string;
  defTD: string;
  sacks: string;
  ydsAllowed: string;
  passingYardsAllowed?: string;
  rushingYardsAllowed?: string;
  fumblesRecovered: string;
  ptsAllowed: string;
}

// #region BE Model Replica
export interface ITeamsGameStats {
  away: ITeamGameStats;
  home: ITeamGameStats;
}

export interface IScoringPlay {
  score: string;
  scorePeriod: string;
  homeScore: string;
  awayScore: string;
  teamID: string;
  scoreDetails: string;
  scoreType: string;
  scoreTime: string;
  team: string;
  playerIDs: string[];
}

export interface IDSTStats {
  teamAbv: string;
  teamID: string;
  defTD: string;
  defensiveInterceptions: string;
  sacks: string;
  ydsAllowed: string;
  passingYardsAllowed?: string;
  rushingYardsAllowed?: string;
  fumblesRecovered: string;
  ptsAllowed: string;
  safeties: string;
}

export interface IDSTGameStats {
  away: IDSTStats;
  home: IDSTStats;
}

export interface ITeamLineScore {
  Q1: string;
  Q2: string;
  Q3: string;
  Q4: string;
  OT?: string;
  teamID: string;
  currentlyInPossession?: string;
  totalPts: string;
  teamAbv: string;
}

export interface ILineScore {
  period: string;
  gameClock: string;
  away: ITeamLineScore;
  home: ITeamLineScore;
}

export interface IRushingStats {
  rushAvg: string;
  rushYds: string;
  carries: string;
  longRush: string;
  rushTD: string;
}

export interface IReceivingStats {
  receptions: string;
  recTD: string;
  longRec: string;
  targets: string;
  recYds: string;
  recAvg: string;
}

export interface IPassingStats {
  qbr: string;
  rtg: string;
  sacked: string;
  passAttempts: string;
  passAvg: string;
  passTD: string;
  passYds: string;
  int: string;
  passCompletions: string;
}

export interface IDefensiveStats {
  totalTackles: string;
  defTD: string;
  forcedFumbles: string;
  soloTackles: string;
  tfl: string;
  qbHits: string;
  defensiveInterceptions: string;
  defensiveInterceptionsYards?: string;
  interceptionTDs?: string;
  sacks: string;
  passDeflections: string;
}

export interface IPuntingStats {
  puntYds: string;
  punts: string;
  puntAvg: string;
  puntsin20: string;
  puntTouchBacks: string;
  puntLong: string;
}

export interface IKickingStats {
  fgLong: string;
  fgMade: string;
  fgAttempts: string;
  xpMade: string;
  fgPct: string;
  kickingPts: string;
  xpAttempts: string;
  fgMissed: string;
  xpMissed: string;
}

export interface IKickOffStats {
  kickYards: string;
}

export interface IKickReturnStats {
  kickReturns: string;
  kickReturnTD: string;
  kickReturnYds: string;
  kickReturnAvg: string;
  kickReturnLong: string;
}

export interface IFantasyPointsDefault {
  standard: string;
  PPR: string;
  halfPPR: string;
}

export interface IPlayerGameStats {
  gameID: string;
  playerID: string;
  teamID: string;
  Rushing?: IRushingStats;
  Passing?: IPassingStats;
  Receiving?: IReceivingStats;
  Defense?: IDefensiveStats;
  Punting?: IPuntingStats;
  Kicking?: IKickingStats | IKickReturnStats | IKickOffStats;
  team: string;
  teamAbv: string;
  longName: string;
  fantasyPoints?: string;
  fantasyPointsDefault?: IFantasyPointsDefault;
}

export interface IPlayerGameStatsCollection {
  [key: PlayerID]: IPlayerGameStats;
}

export interface IGameStatsError {
  error: string;
}

export interface IGameInfo {
  homeTeamID: TeamID;
  awayTeamID: TeamID;
  homeAbrv: TeamAbrvType;
  awayAbrv: TeamAbrvType;
  gameID: GameID;
}

// export interface IGameStats extends IGameStatsError {
//   allPlayByPlay?: object;
//   arena: string;
//   arenaCapacity: string;
//   attendance: string;
//   away: string;
//   awayPts: string;
//   awayResult: string;
//   currentPeriod: string;
//   DST: IDSTGameStats;
//   gameClock: string;
//   gameDate: string;
//   gameID: string;
//   gameLocation: string;
//   gameStatus: string;
//   gameStatusCode: string;
//   gameWeek: string;
//   home: string;
//   homePts: string;
//   homeResult: string;
//   lineScore: ILineScore;
//   network: string;
//   playerStats?: IPlayerGameStatsCollection;
//   Referees?: string;
//   scoringPlays: IScoringPlay[];
//   seasonType: string;
//   teamIDAway: string;
//   teamIDHome: string;
//   teamStats: ITeamsGameStats;
//   seasonWeekID: SeasonWeekID;
// }
// #endregion

export interface IGameStatsCollection {
  [key: GameID]: IGameStats;
}
// #endregion

// #region TEAM STATS INTERFACES
// export interface IEnhancedTeamStats extends ITeamGameStats, IDSTStats {
//   /** `{uID}` is a combination of 4 values that uniquely identify a game and team
//    *  `${weekID}-${teamID}-${teamAbrv}-${gameID}`
//    */
//   uID: UID;
//   gameID: GameID;
//   weekID: WeekID;
//   seasonWeekID: SeasonWeekID;
// }

export type ITeamsStatsCollection = {
  [key in TeamID]: IEnhancedTeamStats;
};

export type TeamSeasonStats = {
  [key in GameWeek]: ITeamsStatsCollection;
};

export interface ISeasonStats {
  teamsStats: TeamSeasonStats;
}
// #endregion

// #region RANKINGS INTERFACES
export interface IRanking {
  teamName: string;
  teamID: string;
  value: number;
  isRankTied?: boolean;
}

export type TeamRankInfo = {
  teamValue: number;
  teamAvg: number;
  teamRank: number;
  teamID: TeamID;
  bestTeam?: IRanking;
  worstTeam?: IRanking;
  isRankTied?: boolean;
  highOrLow?: BestRankHighOrLow;
  bestAvg?: number;
  worstAvg?: number;
  valueDivisor?: number;
};

export interface ITeamWeekRank {
  teamName: string;
  teamID: TeamID;
  value: number;
  rank: number;
  isRankTied: boolean;
  rankCategory: string;
  week: GameWeek;
  bestTeam: IRanking;
  worstTeam: IRanking;
  formatUnit?: FormatUnitType;
  result?: ITeamSchedule;
  highOrLow?: BestRankHighOrLow;
  isByeWeek?: boolean;
}

export interface IWeekRank {
  rankCategory: CategoryNameT;
  ranking: IRanking[];
  adjustedRanking?: ITeamWeekRank[];
  weekID: GameWeek;
}

export enum CategoryName {
  DefensiveInterceptions = 'Defensive Interceptions',
  DefensiveTDs = 'Defensive TDs',
  FumblesRecovered = 'Fumbles Recovered',
  FumblesLost = 'Fumbles Lost',
  InterceptionsThrown = 'Interceptions Thrown',
  PassAttempts = 'Pass Attempts',
  PassCompletions = 'Pass Completions',
  PassingPercentage = 'Passing Percentage',
  PassingYards = 'Passing Yards',
  PassingYardsAllowed = 'Passing Yards Allowed',
  PassingYardsPerAttempt = 'Passing Yards Per Attempt',
  Penalties = 'Penalties',
  PenaltyYards = 'Penalty Yards',
  TotalPlays = 'Total Plays',
  PointsAllowed = 'Points Allowed',
  PointsScored = 'Points Scored',
  RushingAttempts = 'Rushing Attempts',
  RushingYards = 'Rushing Yards',
  RushingYardsAllowed = 'Rushing Yards Allowed',
  RushingYardsPerAttempt = 'Rushing Yards Per Attempt',
  Sacks = 'Sacks',
  Safeties = 'Safeties',
  Allowed2ptConversions = 'Allowed 2pt Conversions',
  Scored2ptConversions = 'Scored 2pt Conversions',
  AllowedExtraPoints = 'Allowed Extra Points',
  ScoringEPFor = 'Scored Extra Points',
  AllowedFGs = 'Allowed FGs',
  ScoredFGs = 'Scored FGs',
  AllowedPassingTDs = 'Allowed Passing TDs',
  ScoredPassingTDs = 'Scored Passing TDs',
  AllowedRushingTDs = 'Allowed Rushing TDs',
  ScoredRushingTDs = 'Scored Rushing TDs',
  TotalTDsAllowed = 'Total TDs Allowed',
  TotalTDsScored = 'Total TDs Scored',
  TimeOfPossession = 'Time of Possession',
  TotalYards = 'Total Yards',
  TotalYardsAllowed = 'Total Yards Allowed',
  TotalYardsPerPlay = 'Total Yards Per Play',

  TotalSackedTimes = 'Total Times Sacked',
  TotalSackedYardsLost = 'Total Sacked Yards Lost',
  TotalDrives = 'Total Drives',

  TotalFirstDowns = 'Total First Downs',
  FirstDownsFromPenalties = 'First Downs From Penalties',
  FirstDownsFromRushing = 'First Downs From Rushing',
  FirstDownsFromPassing = 'First Downs From Passing',
  ThirdDownAttempts = 'Third Down Attempts',
  ThirdDownConverted = 'Third Down Converted',
  ThirdDownEfficiency = 'Third Down Efficiency',
  RedZoneAttempts = 'Red Zone Attempts',
  RedZoneScored = 'Red Zone Scored',
  RedZoneEfficiency = 'Red Zone Efficiency',
  FourthDownAttempts = 'Fourth Down Attempts',
  FourthDownScored = 'Fourth Down Converted',
  FourthDownEfficiency = 'Fourth Down Efficiency',
}

export type CategoryNameT = (typeof CategoryName)[keyof typeof CategoryName];

export interface IRankingCategories {
  // OFFENSE
  [key: string]: IWeekRank | undefined;
  totalYards?: IWeekRank; // 1
  totalPlays?: IWeekRank; // 2
  totalYardsPerPlay?: IWeekRank; // 3
  totalPassingYards?: IWeekRank; // 4
  totalPassCompletions?: IWeekRank; // 5
  totalPassAttempts?: IWeekRank; // 6
  totalPassingPercentage?: IWeekRank; // 7
  totalPassingYardsPerAttempt?: IWeekRank; // 8
  totalRushingYards?: IWeekRank; // 9
  totalRushingAttempts?: IWeekRank; // 10
  totalRushingYardsPerAttempt?: IWeekRank; // 11
  totalPointsScored?: IWeekRank; // 12
  totalInterceptionsThrown?: IWeekRank; // 13
  totalFumblesLost?: IWeekRank; // 14
  totalTimeOfPossession?: IWeekRank; // 15
  totalPenalties?: IWeekRank; // 16
  totalPenaltyYards?: IWeekRank; // 17
  // DEFENSE
  totalYardsAllowed?: IWeekRank; // 18
  totalPassingYardsAllowed?: IWeekRank; // 19
  totalRushingYardsAllowed?: IWeekRank; // 20
  totalPointsAllowed?: IWeekRank; // 21
  totalSacks?: IWeekRank; // 22
  totalDefensiveInterceptions?: IWeekRank; // 23
  totalForcedFumbles?: IWeekRank; // 24
  totalSafeties?: IWeekRank; // 25
  totalDefensiveOrSpecialTeamsTds?: IWeekRank; // 26

  totalScoringPassingTDFor?: IWeekRank; // 27
  totalScoringRushingTDFor?: IWeekRank; // 28
  totalScoringFGFor?: IWeekRank; // 29
  totalScoringEPFor?: IWeekRank; // 30
  totalScoring2PtFor?: IWeekRank; // 31
  totalScoringPassingTDAgainst?: IWeekRank; // 32
  totalScoringRushingTDAgainst?: IWeekRank; // 33
  totalScoringFGAgainst?: IWeekRank; // 34
  totalScoringEPAgainst?: IWeekRank; // 35
  totalScoring2PtAgainst?: IWeekRank; // 36
  totalScoringTotalTDFor?: IWeekRank; // 37
  totalScoringTotalTDAgainst?: IWeekRank; // 38

  totalSackedTimes?: IWeekRank; // 39
  totalSackedYardsLost?: IWeekRank; // 40
  totalDrives?: IWeekRank; // 41
  totalThirdDownAttempts?: IWeekRank; // 42
  totalThirdDownConverted?: IWeekRank; // 43
  totalRedZoneScored?: IWeekRank; // 44
  totalRedZoneAttempts?: IWeekRank; // 45
  totalFirstDowns?: IWeekRank; // 46
  totalFirstDownsFromPenalties?: IWeekRank; // 47
  totalFirstDownsFromRushing?: IWeekRank; // 48
  totalFirstDownsFromPassing?: IWeekRank; // 49
  totalFourthDownAttempts?: IWeekRank; // 50
  totalFourthDownScored?: IWeekRank; // 51

  totalThirdDownEfficiency?: IWeekRank; // 52
  totalFourthDownEfficiency?: IWeekRank; // 53
  totalRedZoneEfficiency?: IWeekRank; // 54

  totalPassIncompletions?: IWeekRank; // 55
  totalPassAttemptsAgainst?: IWeekRank; // 56
  totalPassIncompletePercentage?: IWeekRank; // 57
  totalThirdDownAttemptsAgainst?: IWeekRank; // 58
  totalThirdDownStops?: IWeekRank; // 59
  totalThirdDownStopEfficiency?: IWeekRank; // 60
  totalFourthDownStops?: IWeekRank; // 61
  totalFourthDownAttemptsAgainst?: IWeekRank; // 62
  totalFourthDownStopEfficiency?: IWeekRank; // 63
  totalRedZoneStops?: IWeekRank; // 64
  totalRedZoneAttemptsAgainst?: IWeekRank; // 65
  totalRedZoneStopEfficiency?: IWeekRank; // 66
}

// #endregion

// #region CATEGORY INTERFACES
export interface IGameDetailsCategory {
  categoryTitle: string;
  hTeamID: TeamID;
  aTeamID: TeamID;
  highestWeeklyScoreTeamID: TeamID;
  highestSeasonScoreTeamID: TeamID;
  highestWeeklyScoreTied?: boolean;
  highestSeasonScoreTied?: boolean;
  hTeamScore: number;
  aTeamScore: number;
  hTeamSeasonScore: number;
  aTeamSeasonScore: number;
  highestWeeklyScore: number;
  highestSeasonScore: number;
  totalScore: number;
  hTeamWeeklyRankTied?: boolean;
  hTeamSeasonRankTied?: boolean;
  aTeamWeeklyRankTied?: boolean;
  aTeamSeasonRankTied?: boolean;
  aTeamSeasonRank: number;
  hTeamSeasonRank: number;
  aTeamWeeklyRank: number;
  hTeamWeeklyRank: number;
  formatUnit: FormatUnitType;
  navCategory: GameNavCategoryType;
  highOrLow: BestRankHighOrLow;
}

export interface ICompareTeamsCategory {
  categoryTitle: string;
  teamID: TeamID;
  teamScore: number;
  teamRank: number;
  teamRankTied?: boolean;
  teamScoreAvg?: number;
  highestScoreTeamID: TeamID;
  highestScore: number;
  highestScoreTied?: boolean;
  highestScoreAvg?: number;
  formatUnit: FormatUnitType;
  navCategory: GameNavCategoryType;
  highOrLow: BestRankHighOrLow;
}

export type ComparisonCategory = {
  hTeamCategory: ICompareTeamsCategory;
  aTeamCategory: ICompareTeamsCategory;
};
// #endregion

// #region PAYLOADS
export type GameStatsPayload = {
  gameID: GameID;
  stats: IGameStats;
};

export type WeekGamesPayload = {
  week: GameWeek;
  games: IGame[];
};

export type GameSchedulePayload = (IGame | IMatchup)[];
export type GameScheduleByYearPayload = {
  games: (IGame | IMatchup)[];
  yearID: Season;
};
export type MatchupsSchedulePayload = IMatchup[];
export type PredictionsForMatchupsPayload = {
  predictions: Record<GameID, IGamePredictionBase>;
  yearID: SeasonID;
};

/** @deprecated */
export type TeamStatsPayload = {
  teamID: TeamID;
  weekID: WeekID;
  gameID: GameID;
  stats?: IEnhancedTeamStats;
};

export type GameResultsPayload = IGameStats[];

export type TeamResultPayload = IEnhancedTeamStats[];

export type ByeWeekTeamsPayload = ITeamsOnByeWeek[];

/** @deprecated */
export type TeamsWeeklyStatsPayload = {
  teams: ITeamsStatsCollection;
  weekID: WeekID;
};

export type RankingsPayload = {
  rankings: IRankingCategories;
  weekID: WeekID;
  yearID: SeasonID;
  seasonWeekID: SeasonWeekID;
  calculated: boolean;
  seasonRankings: IRankingCategories;
};

export type RankingsPayloadByWeek = {
  [key: SeasonWeekID]: RankingsPayload;
};

/** @deprecated */
export type WeeklyRankCalculatedPayload = {
  isCalculated: boolean;
  weekID: WeekID;
};

/** @deprecated */
export type WeeklyStandingsPayload = {
  standings: ITeamWeeklyResult[];
  weekID: WeekID;
  calculated: boolean;
};

/** @deprecated */
export type WeeklyStandingsCalculatedPayload = {
  isCalculated: boolean;
  weekID: WeekID;
};

/** @deprecated */
export type TeamSchedulePayload = {
  teamID: TeamID;
  teamSchedule: ITeamSchedule;
};

export type UIDSchedulePayload = {
  positionIndex: number;
  schedule: UID[];
  seasonYear: number;
};

// #endregion

// #region IMAGES
export const LOGO_IMAGES_V2 = '/images/nfl/logos/185x165/';
export const LOGO_IMAGES_V1_500 = '/images/nfl/logos/500x500/';
export const LOGO_IMAGES_V1_250 = '/images/nfl/logos/250x250/';
export const LOGO_IMAGES_V1_100 = '/images/nfl/logos/100x100/';
export const LOGO_IMAGES_V1_50 = '/images/nfl/logos/50x50/';
export const APP_LOGO = '/images/nfl/logos/app/football-analytics-logo.webp';
export const APP_BG = '/images/nfl/bg/football-field-bg.webp';
export const SKELETON_LOGO_V72 = '/images/nfl/logos/app/skeleton-72x72.webp';
// #endregion

export type DecomposedUID = {
  yearID: SeasonID;
  weekNum: number;
  teamNum: number;
  weekID: WeekID;
  teamID: TeamID;
  teamAbrv: string;
  gameID: GameID;
  seasonWeekID: SeasonWeekID;
};

// #969696

// #region GAME CONTEXT ENRICHMENT ********************************************

export enum PlayoffsImplications {
  None = 'None',
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
}

export enum VenueInfo {
  Home = 'Home',
  Away = 'Away',
  Neutral = 'Neutral',
}

export enum Precipitation {
  None = 'none',
  Rain = 'rain',
  Snow = 'snow',
  Sleet = 'sleet',
  Hail = 'hail',
  FreezingRain = 'freezing_rain',
}

export enum WindDirection {
  N = 'N',
  NE = 'NE',
  E = 'E',
  SE = 'SE',
  S = 'S',
  SW = 'SW',
  W = 'W',
  NW = 'NW',
  None = 'None',
}

export interface IGameWeather {
  temperature: number; // in fahrenheit
  windSpeed: number; // in mph
  windDirection: string; // "N", "NE", "E", "SE", "S", "SW", "W", "NW"
  precipitation: Precipitation;
  visibility: number; // in miles
}

export interface IGameContextEnrichment {
  // Days of Rest
  homeTeamRestDays: number; // 7; // Days since last game
  awayTeamRestDays: number;

  // Venue Information
  venue: {
    type: VenueInfo; // "home" | "away" | "neutral"
    isIndoor: boolean; // Dome or outdoor
    surface: FieldTurfType; // "grass" | "artificial"
  };

  // Game Classification
  isDivisionGame: boolean; // Division rival
  isPlayoffGame: boolean;
  isRivalryGame: boolean; // Historic rivalry
  primeTime: boolean; // Monday/Thursday/Sunday Night

  // Weather (outdoor only)
  weather: IGameWeather;
  // {
  //   temperature: number; // in fahrenheit
  //   windSpeed: number; // in mph
  //   windDirection: string; // "N", "NE", "E", "SE", "S", "SW", "W", "NW"
  //   precipitation: Precipitation;
  //   visibility: number; // in miles
  // };

  // Season Context
  seasonWeekID: SeasonWeekID;
  playoffImplications: PlayoffsImplications; // "high" | "medium" | "low" | "none"
  gameID: GameID;
}

export type TeamBasicContext = {
  id: string;
  name: string;
  logo: string;
  isIndoor: boolean;
  surface: FieldTurfType;
  isHomeTeam: boolean;
  rivalries: TeamAbrvID[];
};

// #endregion
